<section
  class="account-settings-screen tw-w-full"
  data-cy="account-settings-screen"
>
  <!-- Heading: styled as its own rounded bar (mirrors the
       Promotions bar in ProfileComponent) with a dedicated
       close affordance on the right. -->
  <header
    class="
      heading-section
      tw-flex
      tw-h-[58.5px]
      tw-items-center
      tw-justify-between
      tw-bg-[#F0F0F0]
      tw-rounded-[12px]
      tw-p-[10px_18px]
      tw-mb-[16px]
    "
    data-cy="acc-settings-header"
  >
    <h3
      class="tw-font-bold tw-text-[18px] tw-text-[#000000] tw-tracking-[0.5px] tw-m-0"
      data-cy="acc-settings-title"
    >
      {{ 'ui.account.account.settings' | translate: 'Account Settings' }}
    </h3>
    <button
      type="button"
      (click)="close()"
      class="
        acc-settings-close
        tw-flex
        tw-items-center
        tw-justify-center
        tw-bg-transparent
        tw-border-0
        tw-cursor-pointer
      "
      attr.aria-label="{{ 'ui.account.close.account.settings' | translate: 'Close account settings' }}"
      data-cy="acc-settings-close"
    >
      <mat-icon class="!tw-text-[22px] tw-text-[#000000]">close</mat-icon>
    </button>
  </header>

  <!-- Settings list. We render each row as its own
       mat-expansion-panel so the "active card hides the others"
       UX is achieved purely via the [hidden] binding driven by
       the AccountSettingsService state. -->
  <mat-accordion class="my-settings-section" displayMode="flat">
    @for (card of cards; track trackByCardId($index, card)) {
      <!-- Expandable rows: Wagering Preferences, Wagering Limits,
           Change Password, Favorite Tracks -->
      @if (card.expandable) {
        <mat-expansion-panel
          class="settings-row"
          [ngClass]="{
            'tw-rounded-[12px] tw-border tw-border-[#E0E0E0]!': activeCard === card.id,
            'active': activeCard === card.id
          }"
          [class.active]="activeCard === card.id"
          [hidden]="isRowHidden(card.id)"
          [expanded]="activeCard === card.id"
          (opened)="onCardToggle(card.id, true)"
          (closed)="onCardToggle(card.id, false)"
          [attr.data-cy]="card.dataCy"
          [attr.data-name]="card.id"
        >
          <mat-expansion-panel-header class="settings-row-header">
            <mat-panel-title class="tw-flex tw-items-center">
              <img
                [src]="card.iconLight"
                class="l-mode tw-w-[28px] tw-inline tw-mr-[22px]"
                [alt]="card.label + ' icon (light)'"
              >
              <img
                [src]="card.iconDark"
                class="d-mode tw-w-[28px] tw-inline tw-mr-[22px]"
                [alt]="card.label + ' icon (dark)'"
              >
              <span class="tw-text-[16px] tw-text-[#000000]">
                {{ card.labelKey | translate: card.label }}
              </span>
            </mat-panel-title>
          </mat-expansion-panel-header>

          <!-- Each card's body. Panels are only instantiated while
               their card is the active one, so they refetch their
               preferences every time the user opens them. -->
          @switch (card.id) {
            @case ('wageringPreferences') {
              <div class="card-body tw-py-[12px]" data-cy="wagering-preferences-body">
                @if (activeCard === 'wageringPreferences') {
                  <feature-mat-wagering-preferences></feature-mat-wagering-preferences>
                }
              </div>
            }

            @case ('language') {
              <div class="card-body tw-py-[12px]" data-cy="language-body">
                @if (activeCard === 'language') {
                  <feature-mat-language></feature-mat-language>
                }
              </div>
            }

            @case ('wageringLimits') {
              <div class="card-body tw-py-[12px]" data-cy="wagering-limits-body">
                @if (activeCard === 'wageringLimits') {
                  <feature-mat-wagering-limits></feature-mat-wagering-limits>
                }
              </div>
            }

            @case ('changePassword') {
              <div class="card-body tw-py-[12px]" data-cy="change-password-body">
                <button
                  mat-flat-button
                  color="primary"
                  type="button"
                  class="change-password-btn"
                  (click)="changePasswordPrompt()"
                  data-cy="change-pw-btn"
                >
                  {{ 'account.change.password' | translate: 'Change Login Password' }}
                </button>
              </div>
            }

            @case ('favoriteTracks') {
              <div class="card-body tw-py-[12px]" data-cy="favorite-tracks-body">
                @if (activeCard === 'favoriteTracks') {
                  <feature-mat-favorite-tracks></feature-mat-favorite-tracks>
                }
              </div>
            }
          }
        </mat-expansion-panel>
      }

      <!-- Non-expandable rows — coming-soon actions, rendered as plain
           buttons styled to look like the accordion headers. -->
      @if (!card.expandable) {
        <button
          type="button"
          class="settings-row settings-row-action"
          [hidden]="isRowHidden(card.id)"
          (click)="openComingSoon()"
          [attr.data-cy]="card.dataCy"
          [attr.data-name]="card.id"
        >
          <img
            [src]="card.iconLight"
            class="l-mode tw-inline tw-mr-[19px]"
            [alt]="card.label + ' icon (light)'"
          >
          <img
            [src]="card.iconDark"
            class="d-mode tw-inline tw-mr-[19px]"
            [alt]="card.label + ' icon (dark)'"
          >
          <span class="tw-text-[16px] tw-flex-1 tw-text-left">
            {{ card.labelKey | translate: card.label }}
          </span>
          <mat-icon class="chev-right !tw-text-[#000000]">chevron_right</mat-icon>
        </button>
      }
    }

    <!-- App version + environment (same as login screen). -->
    <div
      class="app-version-row tw-flex tw-items-center tw-justify-center tw-text-[13px]"
      [hidden]="activeCard !== null"
      data-cy="app-version-row"
    >
      <span data-cy="app-version-text">
        {{ 'preference.app.version' | translate: 'Application Version' }} {{ appVersion }}
        {{ environment === 'production' ? '' : (environment | titlecase) }}
      </span>
    </div>

    <!-- Light/Dark Mode toggle. Always visible at the bottom —
         the user expects to flip the theme even while a card is
         open. -->
    <!-- <div
      class="settings-row light-dark-mode-row"
      [hidden]="activeCard !== null"
      data-cy="toggle-mode"
    >
      <mat-slide-toggle
        color="primary"
        [checked]="darkMode"
        (change)="toggleDarkMode($event)"
        data-cy="toggle-mode-input"
        class="tw-mr-[22px]"
      ></mat-slide-toggle>
      <span class="tw-text-[16px]">Light/Dark Mode</span>
    </div> -->
  </mat-accordion>
</section>
